/**
 * FacilitatorControlPanel
 *
 * A compact inline panel visible only to the session facilitator during a live
 * collaborative tool run.  Provides:
 *   - Vote mode toggle  (hidden until reveal | live running tally)
 *   - Reveal votes      (transitions phase: collecting → revealed)
 *   - Capture results   (transitions phase: revealed → finalized; writes session_results)
 *
 * Rendered inside CollaborativeSessionPanel when isFacilitator === true.
 */

import { useState } from 'react';
import { AuthorizationGate } from '../AuthorizationGate';
import {
  Eye,
  EyeOff,
  Flag,
  CheckCheck,
  Loader2,
  Activity,
  LockKeyhole,
  MessageSquare,
} from 'lucide-react';
import { useToolSessionStore } from '../../../stores/prioritization/toolSessionStore';
import type { ToolSessionPhase, VoteMode } from '../../../types/prioritization/toolSession';
import type { ToolResultItem } from '../ToolWrapper';
import type { AggregateResult, FinalDecision } from '../../../types/prioritization/toolSession';

export interface FacilitatorControlPanelProps {
  toolSessionId: string;
  phase: ToolSessionPhase;
  voteMode: VoteMode;
  /** Whether all participants have submitted */
  allSubmitted: boolean;
  submittedCount: number;
  participantCount: number;
  aggregates: AggregateResult[];
  facilitatorId: string;
  facilitatorName: string;
  /** Called after the facilitator finalizes; receives the final results */
  onFinalized: (results: ToolResultItem[]) => void;
}

export function FacilitatorControlPanel({
  toolSessionId,
  phase,
  voteMode,
  allSubmitted,
  submittedCount,
  participantCount,
  aggregates,
  facilitatorId,
  facilitatorName,
  onFinalized,
}: FacilitatorControlPanelProps) {
  const setPhase = useToolSessionStore(s => s.setPhase);
  const setVoteMode = useToolSessionStore(s => s.setVoteMode);
  const setRationalePolicy = useToolSessionStore(s => s.setRationalePolicy);
  const rationalePolicy = useToolSessionStore(
    s => s.toolSessions.find(ts => ts.id === toolSessionId)?.rationalePolicy ?? 'encouraged'
  );
  const finalizeDecision = useToolSessionStore(s => s.finalizeDecision);

  const [busy, setBusy] = useState(false);

  const handleToggleVoteMode = async () => {
    if (phase !== 'collecting') return;
    setBusy(true);
    const next: VoteMode = voteMode === 'hidden' ? 'live' : 'hidden';
    await setVoteMode(toolSessionId, next);
    setBusy(false);
  };

  const handleReveal = async () => {
    if (phase !== 'collecting') return;
    setBusy(true);
    await setPhase(toolSessionId, 'revealed');
    setBusy(false);
  };

  const handleCapture = () => {
    if (phase !== 'revealed' || aggregates.length === 0) return;

    const decision: FinalDecision = finalizeDecision({
      toolSessionId,
      overrides: [],
      decidedBy: facilitatorId,
      decidedByName: facilitatorName,
      aggregates,
    });

    onFinalized(decision.finalResults as ToolResultItem[]);
  };

  const isCollecting = phase === 'collecting';
  const isRevealed = phase === 'revealed';
  const isFinalized = phase === 'finalized';

  return (
    <div className="border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 p-3 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <LockKeyhole className="w-3.5 h-3.5 text-[var(--text-ter)] dark:text-[var(--text-ter)] flex-shrink-0" />
        <span className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide">
          Facilitator controls
        </span>
        {isFinalized && (
          <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--green-dim)] dark:bg-[var(--green)]/40 text-[var(--green)] dark:text-[var(--text-ter)]">
            <CheckCheck className="w-3 h-3" />
            Finalized
          </span>
        )}
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)] overflow-hidden">
          <div
            className="h-full rounded-full bg-[var(--slate)] dark:bg-[var(--slate)] transition-all duration-500"
            style={{ width: participantCount > 0 ? `${(submittedCount / participantCount) * 100}%` : '0%' }}
          />
        </div>
        <span className="text-xs text-[var(--text-sec)] tabular-nums flex-shrink-0">
          {submittedCount}/{participantCount}
        </span>
      </div>

      {/* Controls row */}
      {!isFinalized && (
        <AuthorizationGate
          action="session.facilitate"
          resource={{ type: 'session' as const, id: toolSessionId }}
        >
        <div className="flex flex-wrap gap-2">
          {/* Vote mode toggle — only during collecting phase */}
          {isCollecting && (
            <button
              onClick={handleToggleVoteMode}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border text-xs font-medium transition-colors border-[var(--slate)] dark:border-[var(--slate)] text-[var(--text-ter)] dark:text-[var(--text-ter)] bg-[var(--bg-raised)] dark:bg-[var(--slate)]/30 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/60 disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                voteMode === 'hidden'
                  ? 'Votes are hidden until you reveal. Click to show live tally.'
                  : 'Live tally is visible. Click to hide votes until reveal.'
              }
            >
              {busy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : voteMode === 'hidden' ? (
                <EyeOff className="w-3.5 h-3.5" />
              ) : (
                <Activity className="w-3.5 h-3.5" />
              )}
              {voteMode === 'hidden' ? 'Hidden' : 'Live tally'}
            </button>
          )}

          {/* Rationale policy — how strongly participants are asked to explain "why". Never blocks. */}
          {isCollecting && (
            <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border text-xs font-medium border-[var(--slate)] dark:border-[var(--slate)] text-[var(--text-ter)] dark:text-[var(--text-ter)] bg-[var(--bg-raised)] dark:bg-[var(--slate)]/30"
              title="How strongly participants are asked to explain their reasoning. Never blocks submission."
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Rationale
              <select
                value={rationalePolicy}
                onChange={e => void setRationalePolicy(toolSessionId, e.target.value as typeof rationalePolicy)}
                className="bg-transparent text-xs font-semibold focus:outline-none cursor-pointer"
              >
                <option value="off">Off</option>
                <option value="encouraged">Encouraged</option>
                <option value="required">Required</option>
              </select>
            </label>
          )}

          {/* Reveal button — collecting → revealed */}
          {isCollecting && (
            <button
              onClick={handleReveal}
              disabled={busy || submittedCount === 0}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold transition-colors bg-[var(--slate)] hover:bg-[var(--slate)] dark:bg-[var(--slate)] dark:hover:bg-[var(--slate)] text-white disabled:opacity-50 disabled:cursor-not-allowed"
              title={submittedCount === 0 ? 'Wait for at least one submission before revealing' : 'Reveal all votes to participants'}
            >
              {busy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Eye className="w-3.5 h-3.5" />
              )}
              Reveal votes
              {!allSubmitted && submittedCount > 0 && (
                <span className="opacity-70">({submittedCount} so far)</span>
              )}
            </button>
          )}

          {/* Capture results — revealed → finalized */}
          {isRevealed && (
            <div className="flex items-center gap-2 w-full">
              <div className="flex-1 text-xs text-[var(--text-sec)]">
                Votes are visible to all participants.
              </div>
              <button
                onClick={handleCapture}
                disabled={aggregates.length === 0}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold transition-colors bg-[var(--green)] hover:bg-[var(--green)] dark:bg-[var(--green)] dark:hover:bg-[var(--green)] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                title="Accept the aggregate results and save them to the session"
              >
                <Flag className="w-3.5 h-3.5" />
                Capture results
              </button>
            </div>
          )}
        </div>
        </AuthorizationGate>
      )}
    </div>
  );
}
